import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Markdown, { defaultUrlTransform } from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
// remarkBreaks removed: it converted every single \n to <br>, breaking normal
// markdown paragraph flow. Standard markdown treats \n\n as paragraph breaks
// and single \n as continuation — which is what we want for LLM output.
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { Highlight, Prism } from "prism-react-renderer";

type Props = {
  /** Raw assistant text (Markdown-ish) */
  content: string;
  /** Optional: add a class on the wrapper */
  className?: string;
  /** Optional inline element rendered after the markdown output (e.g. a copy button) */
  trailingInline?: React.ReactNode;
  /** Workspace module labels that should be clickable when rendered in bold */
  moduleLabels?: string[];
  /** Called when a clickable module label is clicked */
  onModuleLabelClick?: (label: string, occurrenceIndex: number) => void;
  /** Called when a workspace object link (xavior://object/type/id) is clicked */
  onObjectClick?: (objectType: string, objectId: string) => void;
  /** Called when an autolinked email address is clicked. Receives the bare address (no mailto:). */
  onEmailClick?: (email: string) => void;
  /** Called when an autolinked phone number is clicked. Receives the bare number (no tel:). */
  onPhoneClick?: (phone: string) => void;
};

const EMPTY_PRISM_THEME = { plain: {}, styles: [] } as const;

// Map common fence aliases -> Prism language ids
const LANG_ALIASES: Record<string, string> = {
  js: "javascript",
  ts: "typescript",
  jsx: "jsx",
  tsx: "tsx",
  py: "python",
  rb: "ruby",
  sh: "bash",
  shell: "bash",
  yml: "yaml",
  md: "markdown",
  jsonc: "json",
};

function normalizeLang(raw?: string): string | undefined {
  if (!raw) return undefined;
  const lowered = raw.toLowerCase().trim();
  const mapped = LANG_ALIASES[lowered] ?? lowered;

  const prismAny = Prism as any;
  if (prismAny?.languages?.[mapped]) return mapped;

  return undefined;
}

function safeUrlTransform(url: string): string {
  const safe = defaultUrlTransform(url);
  if (url.startsWith("tel:")) return url;
  if (url.startsWith("xavior://")) return url;
  return safe;
}

/** Parse xavior://object/{type}/{id} links. Returns null if not a match. */
function parseObjectLink(href: string): { objectType: string; objectId: string } | null {
  const match = href.match(/^xavior:\/\/object\/([a-z]+)\/(.+)$/);
  if (!match) return null;
  return { objectType: match[1], objectId: match[2] };
}

function isProbablyExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/** @internal Exported for unit tests only — not part of the public API. */
export function _normalizePlainAssistantSegment(input: string): string {
  return normalizePlainAssistantSegment(input);
}

// Collapse a `**bold**` span whose markers are split across a soft line break
// (e.g. `**Summary\n**`). Under CommonMark a `**` adjacent to a newline is not
// flanking and can't open/close emphasis, so it renders as literal asterisks.
// Limited to short spans (≤2 newlines, ≤120 chars) so we don't merge genuinely
// separate paragraphs that merely sit between two bold spans (issue #3037).
function collapseLineBrokenBold(input: string): string {
  return input.replace(/\*\*([\s\S]*?)\*\*/g, (match, inner: string) => {
    if (!inner.includes('\n')) return match; // already single-line, leave alone
    const newlineCount = (inner.match(/\n/g) || []).length;
    if (newlineCount > 2 || inner.length > 120) return match; // too long, likely mismatched
    const collapsed = inner.replace(/\s*\n\s*/g, ' ').trim();
    if (!collapsed) return match; // empty bold, leave alone
    return `**${collapsed}**`;
  });
}

// The model frequently separates a heading, a bold section-label, and a body
// paragraph with a SINGLE newline. CommonMark treats a lone `\n` as a soft break
// (collapsed to a space), so those blocks glue together — a bold heading runs
// into its paragraph, or (worst case) the whole answer renders as one block.
// Promote a lone `\n` to a paragraph break, but ONLY between two block-level
// prose lines: the first line must end a block (sentence-terminal punctuation,
// an ATX `#` heading, or a standalone `**bold**` heading), and neither line may
// be a list item, table row, blockquote, indented/fenced code, or a line with an
// unclosed bold span (a wrapped continuation). This leaves genuine markdown
// structure — and already-`\n\n`-separated paragraphs — untouched.
function promoteParagraphNewlines(input: string): string {
  if (!input.includes('\n')) return input;
  const lines = input.split('\n');
  const isStructural = (s: string): boolean => {
    const t = s.trimStart();
    return (
      /^([-*+•]\s|\d{1,3}[.)]\s|\||>)/.test(t) || // list / table / blockquote
      /^ {4,}\S/.test(s) // indented code
    );
  };
  const out: string[] = [];
  let inFence = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    out.push(line);
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
    if (inFence) continue;
    const next = lines[i + 1];
    if (next === undefined) continue;
    if (line.trim() === '' || next.trim() === '') continue; // already a blank boundary
    if (isStructural(line) || isStructural(next)) continue; // protect lists/tables/quotes/code
    if ((line.match(/\*\*/g) || []).length % 2 === 1) continue; // wrapped/unclosed bold
    // The model writes each heading/paragraph on its own line and does NOT
    // soft-wrap, so a lone newline is a paragraph boundary — UNLESS the next line
    // starts lowercase, which signals a wrapped sentence continuation. New blocks
    // (headings, bold labels, new sentences) start with #, *, a digit, or a capital.
    if (/^[a-z]/.test(next.trimStart())) continue;
    out.push(''); // promote the lone newline to a paragraph break
  }
  return out.join('\n');
}

function normalizePlainAssistantSegment(input: string): string {
  if (!input) return input;
  let output = input;

  // Fix headings missing space after #: "#Header" → "# Header", "##Title" → "## Title"
  output = output.replace(/^(#{1,6})([^\s#])/gm, "$1 $2");

  // Ensure markdown headings (##) get a paragraph break before them.
  // Headings need a blank line before them to be parsed as headings by CommonMark.
  output = output.replace(/([^\n])\n(#{1,6}\s)/g, "$1\n\n$2");
  // Also handle headings with no preceding newline at all (glued to text).
  output = output.replace(/([^\n\s#])\s*(#{1,6}\s)/g, "$1\n\n$2");

  // `---` thematic-break dividers that the model glues to the end of a prose
  // line (e.g. "...mass slaughter.---") otherwise render as literal trailing
  // dashes. Pull a run of 3+ hyphens onto its own line with blank lines around
  // it so CommonMark renders a real <hr>. Require a non-space char immediately
  // before AND end-of-line right after, so we never disturb a standalone `---`
  // line, a setext-heading underline (alone under its text), or an inline em-dash
  // / hyphenated word ("well---known" keeps text after the dashes, so no match).
  output = output.replace(/(\S)[ \t]*-{3,}[ \t]*(?=\n|$)/g, "$1\n\n---\n\n");

  // Fix bold markers split across a soft line break (e.g. `**Summary\n**`).
  // Run here so later structural rules see well-formed bold; also run again at
  // the very end, because some \n\n-insertion rules below can re-break a pair.
  output = collapseLineBrokenBold(output);

  // Fix bold markers with extra whitespace: "** text**" or "**text **" → "**text**"
  // Must run before italic fixes to avoid single-* regexes eating ** boundaries.
  // Use lookbehind to ensure the opening ** isn't preceded by a word char (which would mean it's closing **).
  // Use [^\S\n] (horizontal whitespace only) to avoid matching across newlines.
  output = output.replace(/(?<!\w)\*\*[^\S\n]+([^*\n]{1,50}?)[^\S\n]*\*\*(?!\*)/g, (_, inner: string) => `**${inner.trim()}**`);
  output = output.replace(/(?<!\w)\*\*([^*\s\n][^*\n]{0,50}?)[^\S\n]+\*\*(?!\*)/g, (_, inner: string) => `**${inner.trim()}**`);

  // Fix italic markers with extra whitespace: "* text*" or "*text *" → "*text*"
  // Use lookbehind/lookahead to avoid matching * that is part of ** bold markers.
  output = output.replace(/(?<!\*)\*\s+([^*\n][^*\n]*?)\s*\*(?!\*)/g, (_, inner: string) => `*${inner.trim()}*`);
  output = output.replace(/(?<!\*)\*([^*\n]+?)\s+\*(?!\*)/g, (_, inner: string) => `*${inner.trim()}*`);

  // Inline bold "definition labels" with the colon OUTSIDE the bold — haiku's
  // flowing style: "...genuinely idealistic. **Path to power**: He joined the
  // Jacobin Club... **The Terror**: His conviction..." glues several labelled
  // sections into one wall. Also handles em-dash/en-dash variants: "**Area
  // codes & trivia** — description" (issue #3386). Promote each to its own
  // paragraph. Gate hard to a real section label: must follow a sentence
  // terminator + space, the bold must start uppercase and be short (≤48 chars),
  // and a separator (colon, em-dash, or en-dash) must follow the close `**` —
  // so ordinary inline emphasis ("...the **general will**: a note") is left
  // untouched (it isn't preceded by a sentence terminator).
  // MUST run before the bold-heading-glue rule below: once each label is split
  // onto its own paragraph, that rule can no longer mis-pair one label's closing
  // `**` with the next label's opening `**` (the `[^*\n]` span can't cross \n).
  output = output.replace(
    /([.!?])[ \t]+(\*\*[A-Z][^*\n]{1,48}\*\*[ \t]*[:—–])/g,
    "$1\n\n$2",
  );

  // Fix dash glued to bold markers in list items: "-**text**" → "- **text**"
  output = output.replace(/^(\s*)-\*\*/gm, '$1- **');

  // Fix unspaced dash list items: "-item" → "- item" (only at line start)
  output = output.replace(/^(\s*)-([^\s\d-])/gm, '$1- $2');

  // Fix lone "-*" or "-*\n" at line start (broken list+italic combo) — normalize to "- "
  output = output.replace(/^(\s*)-\*\s*$/gm, '$1-');
  output = output.replace(/^(\s*)-\*\s+/gm, '$1- ');

  // Fix first list item glued to intro text via dash/en-dash:
  // "You have 4 contacts:– Betty" or "contacts:- Betty" → proper list
  output = output.replace(/(:)[–—-]\s*/g, '$1\n\n- ');

  // Fix first list item glued to intro text without dash (just newline missing before first bullet):
  // "at Greylock:\n- Name" should have a blank line; "at Greylock:Name" needs ":\n\n- Name"
  // Already handled by markdown rules if there's a newline, but catch the no-newline case:
  output = output.replace(/(:\s*)(\*\*[^*\n]+\*\*)\n(\s*[-•])/g, '$1\n\n- $2\n$3');

  // Fix trailing text merged into last bullet item. The LLM often appends a follow-up
  // sentence to the last list item instead of putting it in a new paragraph.
  // Detect sentence-start phrases after list item content and split into new paragraph.
  const trailingPhrases = 'Do you|Want me|Would you|You also|You have|You\'re|Is there|Should I|That\'s|I also|I can|I don\'t|There\'?s also|Any |Need |Let me|Shall I|Happy to|Want to|Note:|Additionally';
  output = output.replace(
    new RegExp(`^(\\s*[-•]\\s+.{5,}?)\\s+((?:${trailingPhrases})[^\\n]*)`, 'gm'),
    '$1\n\n$2',
  );

  // Strip standalone * or ** or *** on their own line (orphaned markers).
  output = output.replace(/^\s*\*{1,3}\s*$/gm, '');

  // Normalize *** (triple star) — usually a mangled bold-italic attempt.
  // "**text ***" → "**text**", "***text**" → "**text**"
  output = output.replace(/\*\*([^*\n]+?)\s*\*{3,}/g, '**$1**');
  output = output.replace(/\*{3,}([^*\n]+?)\*\*/g, '**$1**');

  // Strip ** or * at start of line when followed by whitespace (orphaned opening markers).
  output = output.replace(/^(\s*)\*{1,2}\s+(?!\*)/gm, '$1');

  // Strip lone ** in the middle of text that aren't valid bold markers.
  // Pattern: punctuation, then whitespace, then **, then space — these are orphaned opening **.
  // Require whitespace before ** to avoid stripping closing ** like "Jr.**".
  output = output.replace(/([.!?:,;])\s+\*\*\s+/g, '$1 ');

  // Strip ** that wraps multi-line sections (>2 lines between ** pairs).
  // These are never valid bold — markdown bold must be inline.
  output = output.replace(/\*\*([\s\S]*?)\*\*/g, (match, inner: string) => {
    const newlines = (inner.match(/\n/g) || []).length;
    if (newlines <= 2) return match; // short spans are handled elsewhere
    return inner; // strip the ** wrapper, keep content
  });

  // Fix orphaned opening ** at end of text (streaming partial) — remove them.
  // Only strip if the ** count (including trailing) is odd, meaning the last ** is unmatched.
  if (/\*\*\s*$/.test(output)) {
    const allCount = (output.match(/\*\*/g) || []).length;
    if (allCount % 2 === 1) {
      output = output.replace(/\*\*\s*$/, '');
    }
  }

  // Strip orphaned ** at start of line (with optional bullet prefix) with no matching close on same line.
  output = output.replace(/^(\s*(?:-\s+)?)\*\*(?!\*)(\S[^*\n]*?)$/gm, (match, prefix: string, content: string) => {
    // Only strip if this line has an odd number of ** (unmatched opening)
    const starPairs = (match.match(/\*\*/g) || []).length;
    if (starPairs % 2 === 1) return `${prefix}${content}`;
    return match;
  });

  // Strip orphaned closing ** mid-line (no opening ** earlier on the same line).
  // e.g., "- World Wide Web invented** by Tim" → "- World Wide Web invented by Tim"
  output = output.replace(/^([^*\n]+?)\*\*(\s+\S)/gm, (match, before: string) => {
    // Only strip if there's no opening ** before this closing **
    if (before.includes('**')) return match;
    return match.replace('**', '');
  });

  // Strip orphaned **- or :**- patterns on the SAME line (bold marker glued to colon/dash).
  // e.g., "World Leaders:**-" → "World Leaders:"
  // Use [^\S\n]* to avoid crossing newlines (which would merge section headers with bullets).
  output = output.replace(/:\*\*[^\S\n]*-/g, ':');

  // Insert missing separators before glued ordered list markers (e.g., "modules1. X").
  output = output.replace(/([^\n\d])(?=(\d{1,3}\.(?:\s+)?(?:\*\*|[^\s\d])))/g, "$1\n");
  output = output.replace(/(^|[\s(])(\d{1,3})\.(?=[^\s\d])/gm, "$1$2. ");

  // Ensure section labels and numbered lists are separated.
  output = output.replace(/(:)\s*(\d{1,3}\.\s+)/g, "$1\n$2");
  // Break module count summaries into clean lines (e.g., "Tasks:20 tasks").
  output = output.replace(/(:)\s*([A-Z][A-Za-z0-9'’ _-]{1,80}:\s*\d+)/g, "$1\n$2");
  // Split glued bullets after count summaries.
  output = output.replace(/(\b\d+\s+[A-Za-z][A-Za-z ]{0,20})(\s*-\s+[A-Z])/g, "$1\n$2");

  // Promote single-newline-separated prose blocks (heading / bold label / body)
  // into real paragraph breaks. The model routinely uses lone `\n` where markdown
  // needs `\n\n`, which otherwise renders the whole answer as one glued block.
  output = promoteParagraphNewlines(output);

  // Hide internal IDs in user-visible text.
  // Strategy: protect xavior://object/<type>/<uuid> link URLs with opaque
  // placeholders before stripping bare UUIDs, then restore them afterwards.
  // This avoids over-broad lookbehinds that would also preserve UUIDs in
  // unrelated slash-prefixed contexts (file paths, REST examples, etc.).
  // Use a placeholder sentinel that cannot appear in any LLM-generated markdown.
  // We use a long pseudo-private-use string rather than control characters to
  // satisfy the no-control-regex lint rule while still being unique enough
  // that it can never collide with real content.
  const XOBJ_SENTINEL = '\u{E0078}\u{E006F}\u{E0062}\u{E006A}';
  const xaviorLinkPlaceholders: string[] = [];
  output = output.replace(
    /\(xavior:\/\/object\/[a-z]+\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)/gi,
    (match) => {
      const idx = xaviorLinkPlaceholders.length;
      xaviorLinkPlaceholders.push(match);
      return `${XOBJ_SENTINEL}${idx}${XOBJ_SENTINEL}`;
    },
  );

  // UUIDs in parentheses: (ID: xxxxxxxx-xxxx-...)
  output = output.replace(
    /\s*\((?:ID:\s*)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)/gi,
    "",
  );
  // Bare UUIDs on their own line
  output = output.replace(/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/gim, "");
  // Bare UUIDs inline — match at any word boundary so trailing punctuation
  // (e.g. "Updated contact 08d6b7d3-...-ce8db91706b7.") is also caught.
  // Also collapse the preceding space so the resulting text isn't littered
  // with double spaces or a lone space before a period.
  output = output.replace(/\s*\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "");

  // Restore xavior://object link URLs now that bare UUIDs have been stripped.
  // Sentinel chars are in the Unicode Tag block (U+E0000..U+E007F) — used for
  // language tags, never generated by LLMs, safe to round-trip through JS strings.
  output = output.replace(
    new RegExp(`${XOBJ_SENTINEL}(\\d+)${XOBJ_SENTINEL}`, 'g'),
    (_, idx) => xaviorLinkPlaceholders[Number(idx)],
  );
  output = output.replace(/\s*\((?:ID:\s*)?[A-Za-z0-9_-]{16,}\)/g, "");
  output = output.replace(/\b(moduleId|module_id|conversationId|conversation_id)\s*[:=]\s*[A-Za-z0-9_-]{6,}\b/gi, "$1: [hidden]");
  output = output.replace(/^\s*ID:\s*[A-Za-z0-9_-]{6,}\s*$/gim, "");

  // Remove assistant-only parenthetical disambiguators from visible text.
  output = output.replace(/\s*\(\s*option\s+\d+\s*\)/gi, "");
  output = output.replace(/\s*\(\s*attached to this conversation\s*\)/gi, "");
  output = output.replace(/\s*\(\s*attached here\s*\)/gi, "");
  output = output.replace(/\s*\(\s*not attached here\s*\)/gi, "");
  output = output.replace(/\s*\(\s*another module\s*\)/gi, "");

  // Repair glued sentence boundaries like "text.Next sentence" or "text!What".
  output = output.replace(/([.!?]["')\]]?)([A-Z])/g, "$1 $2");

  // Repair glued word+number boundaries like "and38" or "has12".
  output = output.replace(/([a-zA-Z])(\d{2,})\s/g, "$1 $2 ");

  // Ensure space around complete bold/italic spans glued to adjacent words.
  // Valid bold: **non-space-content** (content must not start or end with space).
  output = output.replace(/([a-zA-Z,;])(\*\*\S[^*\n]*?\S\*\*)/g, "$1 $2");
  output = output.replace(/(\*\*\S[^*\n]*?\S\*\*)([a-zA-Z])/g, "$1 $2");

  // Insert paragraph break before bold headings glued to preceding text (e.g., "300**Mexico**").
  // Exclude # to avoid breaking headings like "## **Bold Title**".
  output = output.replace(/([^\s\n#])(\*\*\S[^*\n]{1,}\*\*)/g, "$1\n\n$2");

  // Bold headings followed directly by a word/dash need a paragraph break after.
  // Require bold content to start and end with non-space (valid **bold**).
  output = output.replace(/(\*\*\S[^*\n]*?\S\*\*)(?=[A-Za-z0-9\u2013\u2014-])/g, "$1\n\n");

  // Ensure bullet items aren't glued to the previous sentence.
  output = output.replace(/([.!?:])\s*(-\s+[A-Za-z(])/g, "$1\n$2");

  // Bold section labels (e.g., "**Title:**") should start their own paragraph.
  // Use [a-zA-Z0-9.!?;,)] to avoid matching bullet dashes like "- **Title:**".
  output = output.replace(
    /([a-zA-Z0-9.!?;,)])\s*(\*\*[A-Z]\S[^*\n]{0,80}:\*\*)(?=\s|$)/g,
    "$1\n\n$2",
  );


  // Final pass: re-collapse any bold pair that a \n\n-insertion rule above
  // re-broke across a line break (e.g. line "...glued bold heading..." can turn
  // `Summary**, **Characters` into `Summary\n\n**, **Characters`). Must be last
  // so the markers end up adjacent to their content for CommonMark flanking.
  output = collapseLineBrokenBold(output);

  return output;
}

function normalizeModuleLabel(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function parseOptionSuffix(value: string): { baseLabel: string; optionIndex: number | null } {
  const trimmed = value.trim();
  const match = /^(.*?)\s*\(\s*option\s+(\d+)\s*\)\s*$/i.exec(trimmed);
  if (!match) {
    return { baseLabel: trimmed, optionIndex: null };
  }
  const parsed = Number.parseInt(match[2], 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return { baseLabel: match[1].trim(), optionIndex: null };
  }
  return { baseLabel: match[1].trim(), optionIndex: parsed - 1 };
}

function stripLeadingOrderedListPrefix(value: string): string {
  const match = value.match(/^\s*\d{1,3}[.)]\s+(.+)$/);
  if (!match) return value.trim();
  return match[1].trim();
}

function escapeRegexLiteral(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function splitLeadingModuleLabel(
  text: string,
  labelByNormalized: Map<string, string>,
): { normalized: string; label: string; suffix: string } | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  for (const [normalized, label] of labelByNormalized.entries()) {
    if (!label) continue;
    const escaped = escapeRegexLiteral(label);
    const pattern = new RegExp(`^(${escaped})(\\s*(?::|-|–|—|\\(|\\[).*)?$`, "i");
    const match = trimmed.match(pattern);
    if (!match) continue;
    return {
      normalized,
      label,
      suffix: (match[2] || "").trimStart(),
    };
  }

  return null;
}

function containsModuleLinkButton(node: React.ReactNode): boolean {
  if (Array.isArray(node)) {
    return node.some((entry) => containsModuleLinkButton(entry));
  }
  if (!React.isValidElement(node)) return false;
  const props = node.props as { className?: string; children?: React.ReactNode };
  if (
    node.type === "button"
    && typeof props.className === "string"
    && props.className.includes("assistant-md-module-link")
  ) {
    return true;
  }
  return containsModuleLinkButton(props.children);
}

function flattenNodeText(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map((entry) => flattenNodeText(entry)).join("");
  }
  if (React.isValidElement(node)) {
    return flattenNodeText((node.props as { children?: React.ReactNode }).children);
  }
  return "";
}

function normalizeAssistantDisplayContent(input: string): string {
  if (!input) return input;
  let output = input.replace(/\r\n/g, "\n");

  // Strip tool output blocks that may have leaked into persisted messages
  output = output
    .replace(/<tool-output[^>]*>[\s\S]*?<\/tool-output>/gi, '')
    .trim();

  // Strip leaked tool-call JSON blocks (e.g., [{"tool_name": "...", "arguments": {...}}])
  // These are internal LLM tool invocations that should never appear in the chat.
  output = output.replace(/\[?\s*\{\s*"tool_name"\s*:\s*"[^"]*"\s*,\s*"arguments"\s*:\s*\{[^}]*\}\s*\}\s*\]?/g, "");

  // Strip {{XAVIOR_UI:...}}, {{XAVIOR_STATUS:...}}, {{XAVIOR_ACTIONS:...}} directives.
  // These are internal UI commands, not user-visible text.
  output = output.replace(/\{\{XAVIOR_[A-Z_]+:[\s\S]*?\}\}/g, "");
  // Also strip partial/incomplete directives (streaming edge case).
  output = output.replace(/\{\{XAVIOR_[A-Z_]+[\s\S]*$/g, "");

  // Convert basic HTML-ish responses to markdown-friendly text.
  if (/<\s*\/?\s*[a-z]/i.test(output)) {
    output = output.replace(/<\s*br\s*\/?\s*>/gi, "\n");
    output = output.replace(/<\/\s*(p|div|section|article|header|footer|blockquote|pre|ul|ol)\s*>/gi, "\n");
    output = output.replace(/<\s*li[^>]*>/gi, "\n- ");
    output = output.replace(/<\/\s*li\s*>/gi, "");
    output = output.replace(/<[^>]+>/g, "");
  }

  // Keep fenced code blocks unchanged; normalize plain prose only.
  output = output
    .split(/(```[\s\S]*?```)/g)
    .map((segment) => (segment.startsWith("```") ? segment : normalizePlainAssistantSegment(segment)))
    .join("");

  return output.replace(/\n{3,}/g, "\n\n").trim();
}

function CodeBlock({
  code,
  language,
}: {
  code: string;
  language?: string;
}) {
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // Fail silently in old browsers / WebViews
    }
  }, [code]);

  const displayLang = (language ?? "text").toLowerCase();
  const prismLang = normalizeLang(language);

  return (
    <div className="md-codeblock" data-language={displayLang}>
      <div className="md-codeblock__header">
        <span className="md-codeblock__lang">{displayLang}</span>
        <button
          type="button"
          className="md-codeblock__copy"
          onClick={onCopy}
          aria-label="Copy code"
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      {prismLang ? (
        <Highlight code={code} language={prismLang as any} theme={EMPTY_PRISM_THEME}>
          {({ tokens, getLineProps, getTokenProps }) => (
            <pre className="md-codeblock__pre">
              <code className={`language-${prismLang}`}>
                {tokens.map((line, i) => (
                  <div key={i} {...getLineProps({ line })}>
                    {line.map((token, key) => (
                      <span key={key} {...getTokenProps({ token })} />
                    ))}
                  </div>
                ))}
              </code>
            </pre>
          )}
        </Highlight>
      ) : (
        <pre className="md-codeblock__pre">
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
}

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: Array.from(
    new Set([
      ...(defaultSchema.tagNames ?? []),
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "hr",
      "input",
    ])
  ),
  protocols: {
    ...defaultSchema.protocols,
    href: [...(defaultSchema.protocols?.href ?? []), 'xavior'],
  },
  attributes: {
    ...defaultSchema.attributes,
    code: [
      ...(defaultSchema.attributes?.code ?? []),
      ["className", /^language-./],
    ],
    input: [
      ...(defaultSchema.attributes?.input ?? []),
      ["type", "checkbox"],
      "checked",
      "disabled",
    ],
  },
};

export const AssistantMarkdown = memo(function AssistantMarkdown({
  content,
  className,
  moduleLabels,
  onModuleLabelClick,
  onObjectClick,
  onEmailClick,
  onPhoneClick,
  trailingInline,
}: Props) {
  const remarkPlugins = useMemo(() => [remarkGfm], []);
  const normalizedContent = useMemo(() => normalizeAssistantDisplayContent(content), [content]);
  const clickableModuleLabelMap = useMemo(() => {
    const labels = Array.isArray(moduleLabels) ? moduleLabels : [];
    const entries = labels
      .map((label) => {
        const trimmed = String(label || "").trim();
        return {
          normalized: normalizeModuleLabel(trimmed),
          label: trimmed,
        };
      })
      .filter((entry) => entry.normalized.length > 0 && entry.label.length > 0)
      .sort((a, b) => b.label.length - a.label.length);
    return new Map(entries.map((entry) => [entry.normalized, entry.label]));
  }, [moduleLabels]);
  const clickableModuleLabelSet = useMemo(() => {
    return new Set(clickableModuleLabelMap.keys());
  }, [clickableModuleLabelMap]);
  const rehypePlugins = useMemo(
    () => [[rehypeSanitize, sanitizeSchema]] as any,
    []
  );

  const components: Components = useMemo(() => {
    // Track repeated clickable labels in visual order so duplicate names
    // (e.g. "Tasks") can be mapped to the exact clicked occurrence.
    const clickableOccurrenceMap = new Map<string, number>();
    // Most-recently-rendered contact link in this message, used to route
    // click-on-email and click-on-phone to the same contact activity feed
    // instead of letting mailto:/tel: escape the app.
    let currentContactLink: { objectType: string; objectId: string } | null = null;
    const getClickableTarget = (
      rawText: string,
    ): { baseLabel: string; occurrenceIndex: number; suffix: string } | null => {
      if (!onModuleLabelClick || clickableModuleLabelSet.size === 0) return null;
      const flattened = rawText.trim();
      if (!flattened) return null;
      const withoutListPrefix = stripLeadingOrderedListPrefix(flattened);
      const { baseLabel, optionIndex } = parseOptionSuffix(withoutListPrefix);
      const prefixMatch = splitLeadingModuleLabel(baseLabel, clickableModuleLabelMap);
      const normalized = prefixMatch
        ? prefixMatch.normalized
        : normalizeModuleLabel(baseLabel);
      if (!normalized || !clickableModuleLabelSet.has(normalized)) return null;
      let occurrenceIndex = optionIndex;
      if (occurrenceIndex === null) {
        const nextOccurrence = (clickableOccurrenceMap.get(normalized) ?? 0) + 1;
        clickableOccurrenceMap.set(normalized, nextOccurrence);
        occurrenceIndex = nextOccurrence - 1;
      }
      if (prefixMatch) {
        return {
          baseLabel: prefixMatch.label,
          occurrenceIndex,
          suffix: prefixMatch.suffix,
        };
      }
      const canonicalLabel = clickableModuleLabelMap.get(normalized) || baseLabel;
      return { baseLabel: canonicalLabel, occurrenceIndex, suffix: "" };
    };

    return {
      a({ href, children, ...props }) {
        const safeHref = href ? safeUrlTransform(href) : undefined;
        if (!safeHref) return <span {...props}>{children}</span>;

        // Handle xavior://object/ links — make them clickable to open the object
        const objectLink = parseObjectLink(safeHref);
        if (objectLink && onObjectClick) {
          if (objectLink.objectType === 'contact') {
            currentContactLink = objectLink;
          }
          return (
            <button
              type="button"
              className="assistant-md-object-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onObjectClick(objectLink.objectType, objectLink.objectId);
              }}
            >
              {children}
            </button>
          );
        }

        // mailto: autolinked by remark-gfm — open the Gmail module's
        // compose view prefilled with this address, instead of escaping the
        // app to the OS mail client.
        if (safeHref.startsWith('mailto:') && onEmailClick) {
          const email = safeHref.slice('mailto:'.length).split('?')[0];
          return (
            <button
              type="button"
              className="assistant-md-object-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onEmailClick(email);
              }}
            >
              {children}
            </button>
          );
        }

        // tel: autolinked by remark-gfm — no phone-dial flow yet; route to
        // the nearest preceding contact card if we have one, otherwise let
        // the OS handle it.
        if (safeHref.startsWith('tel:')) {
          if (onPhoneClick) {
            const phone = safeHref.slice('tel:'.length);
            return (
              <button
                type="button"
                className="assistant-md-object-link"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onPhoneClick(phone);
                }}
              >
                {children}
              </button>
            );
          }
          if (currentContactLink && onObjectClick) {
            const target = currentContactLink;
            return (
              <button
                type="button"
                className="assistant-md-object-link"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onObjectClick(target.objectType, target.objectId);
                }}
              >
                {children}
              </button>
            );
          }
        }

        const external = isProbablyExternalHref(safeHref);

        return (
          <a
            {...props}
            href={safeHref}
            target={external ? "_blank" : undefined}
            rel={external ? "noreferrer noopener" : undefined}
          >
            {children}
          </a>
        );
      },

      strong({ children }) {
        return <strong>{children}</strong>;
      },

      p({ children }) {
        const target = getClickableTarget(flattenNodeText(children));
        if (!target) return <p>{children}</p>;
        return (
          <p>
            <button
              type="button"
              className="assistant-md-module-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onModuleLabelClick?.(target.baseLabel, target.occurrenceIndex);
              }}
            >
              {target.baseLabel}
            </button>
            {target.suffix ? <span>{target.suffix}</span> : null}
          </p>
        );
      },

      li({ children }) {
        if (containsModuleLinkButton(children)) return <li>{children}</li>;
        const target = getClickableTarget(flattenNodeText(children));
        if (!target) return <li>{children}</li>;
        return (
          <li>
            <button
              type="button"
              className="assistant-md-module-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onModuleLabelClick?.(target.baseLabel, target.occurrenceIndex);
              }}
            >
              {target.baseLabel}
            </button>
            {target.suffix ? <span>{target.suffix}</span> : null}
          </li>
        );
      },

      pre({ children }) {
        const childArray = React.Children.toArray(children);
        const first = childArray[0];

        if (
          React.isValidElement(first) &&
          typeof first.type === "string" &&
          first.type === "code"
        ) {
          const className = (first.props as any).className as
            | string
            | undefined;
          const raw = (first.props as any).children;

          const code = Array.isArray(raw)
            ? raw.join("")
            : typeof raw === "string"
              ? raw
              : String(raw ?? "");

          const cleaned = code.replace(/\n$/, "");
          const match = /language-([\w-]+)/i.exec(className ?? "");
          const language = match?.[1];

          return <CodeBlock code={cleaned} language={language} />;
        }

        return <pre>{children}</pre>;
      },

      code({ children, className, ...props }) {
        return (
          <code {...props} className={className}>
            {children}
          </code>
        );
      },

      table({ children }) {
        return (
          <div className="md-table">
            <table>{children}</table>
          </div>
        );
      },
    };
  }, [clickableModuleLabelMap, clickableModuleLabelSet, onModuleLabelClick, onObjectClick, onEmailClick, onPhoneClick]);

  // Post-render quality check: strip residual markdown chars that react-markdown
  // failed to parse (orphaned **, *, ##, etc. visible as literal text in the DOM).
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node: Text | null;
    while ((node = walker.nextNode() as Text | null)) {
      const parent = node.parentElement;
      // Skip code/pre elements — markdown chars are intentional there.
      if (parent?.closest('code, pre')) continue;
      const text = node.textContent || '';
      const cleaned = text
        .replace(/^\*{1,2}\s*/g, '')          // leading ** or * (with optional space)
        .replace(/\s*\*{1,2}$/g, '')          // trailing ** or *
        .replace(/^#{1,6}\s*/g, '')           // leading # not parsed as heading
        .replace(/\s+\*{1,2}\s+/g, ' ')      // mid-text orphaned ** or * between spaces
        .replace(/^\*{1,2}$/g, '');           // text node that is just * or **
      if (cleaned !== text) {
        node.textContent = cleaned;
      }
    }
  }, [normalizedContent]);

  return (
    <div ref={containerRef} className={["assistant-md", trailingInline ? "has-trailing-inline" : "", className].filter(Boolean).join(" ")}>
      <Markdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        urlTransform={safeUrlTransform}
        components={components}
      >
        {normalizedContent}
      </Markdown>
      {trailingInline}
    </div>
  );
});
